import type { ReactNode } from 'react';
import { useRouter } from '@tanstack/react-router';
import { PageLoader } from '@customerio/pluma-components/react';
import Journeys from '@/pages/journeys';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { JourneysHandoffView } from '@/services/router/journeys-handoff-view';
import { MigrationRouteView } from '@/services/router/migration-route-view';
import { SafeNavigate } from '@/services/router/safe-navigate';
import { useMigrationHandoffHref } from '@/services/router/use-migration-handoff-href';
import { useCurrentWorkspace } from '@/context/current-workspace';
import { ProtectedRoute } from '@/services/router/components/protected-route';
import { generatePath } from '@/services/router/generate-path';
import PeopleListPage from './index.page';
import PeopleCreatePage from './create/index.page';
import PeopleNewPage from './new/index.page';
import PeopleMergePage from './merge/index.page';

const LOADING_LABEL = 'Loading…';

/** The migrated URLs a `journeys` twin can forward to. */
type MigratedTarget =
  | '/workspaces/$workspaceId/profiles'
  | '/workspaces/$workspaceId/profiles/create'
  | '/workspaces/$workspaceId/profiles/new'
  | '/workspaces/$workspaceId/profiles/merge';

/**
 * Shared body of the two `journeys` handoffs below: flag off renders the embedded Ember host,
 * flag on forwards to `migratedTarget`.
 */
function useJourneysHandoff(migratedTarget: MigratedTarget) {
  const isMigrated = useFeatureFlag(AccountFeatureFlag.ProfilesMigration);
  const router = useRouter();
  const migratedHref = useMigrationHandoffHref(
    (workspaceId) => router.buildLocation({ to: migratedTarget, params: { workspaceId } }).href,
  );

  return (
    <JourneysHandoffView isMigrated={isMigrated} migratedHref={migratedHref}>
      <Journeys />
    </JourneysHandoffView>
  );
}

/**
 * The old `/journeys/people` and `/journeys/profiles` URLs (the Ember router
 * flag-swaps which segment is canonical per account).
 */
export function JourneysPeopleHandoff() {
  return useJourneysHandoff('/workspaces/$workspaceId/profiles');
}

/**
 * The migrated `/profiles` URL. Flag on → Hydra owns the page. Flag off → this
 * URL has no Ember counterpart (Ember only knows the `journeys` forms), so
 * hand back to the `journeys` twin, which Ember serves (canonicalizing the
 * segment itself) — the exact inverse of `JourneysPeopleHandoff`. The two
 * can't loop: each fires in the opposite flag state.
 */
export function PeopleListRouteView() {
  const isMigrated = useFeatureFlag(AccountFeatureFlag.ProfilesMigration);
  const router = useRouter();
  const legacyHref = useMigrationHandoffHref(
    (workspaceId) =>
      router.buildLocation({ to: '/workspaces/$workspaceId/journeys/people', params: { workspaceId } }).href,
  );
  // Readiness gates on the resolved workspace, not the URL segment: the
  // page's reads go through `requireWorkspaceId`, which throws while the
  // workspaces list is still loading.
  const workspace = useCurrentWorkspace();

  return (
    <MigrationRouteView isMigrated={isMigrated} legacyHref={legacyHref} isReady={workspace != null}>
      <PeopleListPage />
    </MigrationRouteView>
  );
}

/**
 * The old `/journeys/people/create` and `/journeys/profiles/create` URLs, the
 * add-people twins of {@link JourneysPeopleHandoff}.
 */
export function JourneysPeopleCreateHandoff() {
  return useJourneysHandoff('/workspaces/$workspaceId/profiles/create');
}

/** The `journeys` twins Ember serves for the migrated Profiles child pages. */
type LegacyProfilesTarget =
  | '/workspaces/$workspaceId/journeys/people/create'
  | '/workspaces/$workspaceId/journeys/people/new';

type ProfilesChildRouteViewProps = {
  /** The `journeys` twin this URL hands back to while the flag is off. */
  legacyTarget: LegacyProfilesTarget;
  children: ReactNode;
};

/**
 * The shell of a migrated Profiles child URL (`/profiles/create`, `/profiles/new`).
 * Flag off it hands back to the `journeys` twin that Ember serves, the inverse
 * of the `journeys` handoffs above. Flag on, the page renders behind the same
 * gate Ember's routes apply: `redirectIfNoPermission(this, 'journeys/people/create')`,
 * which sends a denied viewer to the profiles list.
 */
function ProfilesChildRouteView({ legacyTarget, children }: ProfilesChildRouteViewProps) {
  const isMigrated = useFeatureFlag(AccountFeatureFlag.ProfilesMigration);
  const router = useRouter();
  const legacyHref = useMigrationHandoffHref(
    (workspaceId) => router.buildLocation({ to: legacyTarget, params: { workspaceId } }).href,
  );
  const workspace = useCurrentWorkspace();

  return (
    <MigrationRouteView isMigrated={isMigrated} legacyHref={legacyHref} isReady={workspace != null}>
      {/*
        Redirect target is built from the resolved workspace rather than `ProtectedRoute`'s route
        params: `MigrationRouteView` already gated on that workspace above, and the `last` sentinel
        is only a fallback for the window before it resolves.
      */}
      <ProtectedRoute
        permission="journeys/people/create"
        redirectTo={generatePath('/workspaces/:workspaceId/profiles', { workspaceId: workspace?.id ?? 'last' })}
      >
        {children}
      </ProtectedRoute>
    </MigrationRouteView>
  );
}

/** The migrated `/profiles/create` URL — the add-people catalog. */
export function PeopleCreateRouteView() {
  return (
    <ProfilesChildRouteView legacyTarget="/workspaces/$workspaceId/journeys/people/create">
      <PeopleCreatePage />
    </ProfilesChildRouteView>
  );
}

/**
 * The old `/journeys/people/new` and `/journeys/profiles/new` URLs — the
 * add-a-person twins of {@link JourneysPeopleHandoff}. The add-people catalog
 * in the embedded app still links here.
 */
export function JourneysPeopleNewHandoff() {
  return useJourneysHandoff('/workspaces/$workspaceId/profiles/new');
}

/** The migrated `/profiles/new` URL — the add-a-person form. */
export function PeopleNewRouteView() {
  return (
    <ProfilesChildRouteView legacyTarget="/workspaces/$workspaceId/journeys/people/new">
      <PeopleNewPage />
    </ProfilesChildRouteView>
  );
}

/**
 * The old `/journeys/people/merge` and `/journeys/profiles/merge` URLs — the
 * merge-wizard twins of {@link JourneysPeopleHandoff}. The embedded app's person
 * page and activity log, and the copilot's suggestions, still link here.
 */
export function JourneysPeopleMergeHandoff() {
  return useJourneysHandoff('/workspaces/$workspaceId/profiles/merge');
}

/**
 * The migrated `/profiles/merge` URL — the merge-two-people wizard. Unlike the
 * add-people pages there is no permission gate at the route: a viewer without
 * `journeys/people/edit` sees the wizard with its controls disabled.
 */
export function PeopleMergeRouteView() {
  const isMigrated = useFeatureFlag(AccountFeatureFlag.ProfilesMigration);
  const router = useRouter();
  const legacyHref = useMigrationHandoffHref(
    (workspaceId) =>
      router.buildLocation({ to: '/workspaces/$workspaceId/journeys/people/merge', params: { workspaceId } }).href,
  );
  const workspace = useCurrentWorkspace();

  return (
    <MigrationRouteView isMigrated={isMigrated} legacyHref={legacyHref} isReady={workspace != null}>
      <PeopleMergePage />
    </MigrationRouteView>
  );
}

/**
 * The old `/journeys/people/welcome` and `/journeys/profiles/welcome` URLs.
 * Ember's `people.welcome` route is a bare `replaceWith('people')`, so both
 * spellings forward straight to the Profiles list.
 */
export function JourneysPeopleWelcomeHandoff() {
  return useJourneysHandoff('/workspaces/$workspaceId/profiles');
}

/**
 * The migrated `/profiles/welcome` URL. There is no welcome screen: like
 * Ember's route it replaces itself with the Profiles list. Flag off it hands
 * back to the `journeys` twin, which Ember serves and redirects the same way.
 * Either way this URL only ever redirects, so it is a single replace-navigate
 * to whichever target the flag selects rather than a `MigrationRouteView`
 * around a page.
 */
export function PeopleWelcomeRouteView() {
  const isMigrated = useFeatureFlag(AccountFeatureFlag.ProfilesMigration);
  const router = useRouter();
  const to = useMigrationHandoffHref(
    (workspaceId) =>
      router.buildLocation({
        to: isMigrated ? '/workspaces/$workspaceId/profiles' : '/workspaces/$workspaceId/journeys/people/welcome',
        params: { workspaceId },
      }).href,
  );

  if (to == null) {
    return <PageLoader label={LOADING_LABEL} />;
  }

  return <SafeNavigate to={to} options={{ shouldReplaceHistoryEntry: true }} />;
}
